import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ImagePlus, Lock, Newspaper, Send } from 'lucide-react';
import { toast } from 'sonner';
import PageHero from '@/components/common/PageHero';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { useStore } from '@/contexts/StoreContext';
import { canPublishNews, newsService } from '@/lib/services/news.service';
import { Caixa } from '@/construtor/caixa';

const TIPOS = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_BANNER = 5 * 1024 * 1024;

/**
 * Publicar notícia pela loja.
 *
 * Location: pages
 * Why: a permissão "Publicar Notícias" é do cargo da loja, como as do
 * atendimento de tickets. Quem tem publica por aqui, e a mesma permissão vale
 * no /noticia do Discord (lá, com o cargo vinculado a um cargo do Discord). O
 * texto aceita a mesma formatação do Discord, para quem escreve nos dois
 * lugares não ter de aprender duas.
 */
export default function PublishNewsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { store } = useStore();
  const { isAuthenticated, openLoginModal } = useAuth();

  const [titulo, setTitulo] = useState('');
  const [texto, setTexto] = useState('');
  const [banner, setBanner] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);

  const permitido = isAuthenticated && canPublishNews();

  function escolherBanner(arquivo: File | null) {
    if (arquivo && (!TIPOS.includes(arquivo.type) || arquivo.size > MAX_BANNER)) {
      toast.error(t('store.news_publish.banner_invalid'));
      return;
    }
    setBanner(arquivo);
  }

  async function publicar(e: React.FormEvent) {
    e.preventDefault();
    if (!store?.id || titulo.trim().length < 3 || !texto.trim()) {
      toast.error(t('store.news_publish.fill'));
      return;
    }
    setEnviando(true);
    try {
      const { post, bannerSkipped } = await newsService.publish(store.id, { title: titulo.trim(), content: texto.trim(), banner });
      toast.success(t(bannerSkipped ? 'store.news_publish.published_without_banner' : 'store.news_publish.published'));
      navigate(post.slug ? `/noticias/${post.slug}` : '/');
    } catch (error: any) {
      toast.error(error?.message || t('store.news_publish.error'));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div>
      <PageHero title={t('store.news_publish.title')} breadcrumbs={[{ label: t('store.news_publish.title') }]} />

      <div className="container mx-auto max-w-3xl px-4 py-8 md:px-6 lg:px-8">
        {!permitido ? (
          <Caixa id="noticia.publicar.negado" miolo>
          <Card className="border-border" data-testid="publish-news-denied">
            <CardContent className="flex flex-col items-center gap-4 p-12 text-center">
              <Lock className="h-10 w-10 text-muted-foreground" />
              <p className="text-muted-foreground">{t(isAuthenticated ? 'store.news_publish.not_allowed' : 'store.news_publish.login')}</p>
              {!isAuthenticated && <Button onClick={openLoginModal}>{t('store.header.enter')}</Button>}
            </CardContent>
          </Card>
          </Caixa>
        ) : (
          <Caixa id="noticia.publicar" miolo>
          <Card className="border-border">
            <CardContent className="p-6">
              <Caixa id="noticia.publicar.formulario">
              <form onSubmit={publicar} className="space-y-5" data-testid="publish-news-form">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Newspaper className="h-4 w-4 text-primary" />
                  {t('store.news_publish.intro')}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="news-title">{t('store.news_publish.field_title')}</Label>
                  <Input id="news-title" value={titulo} maxLength={150} onChange={(e) => setTitulo(e.target.value)} required />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="news-content">{t('store.news_publish.field_content')}</Label>
                  <Textarea
                    id="news-content"
                    value={texto}
                    rows={10}
                    maxLength={10000}
                    onChange={(e) => setTexto(e.target.value)}
                    className="resize-y"
                    required
                  />
                  <p className="text-xs text-muted-foreground">{t('store.news_publish.formatting')}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="news-banner">{t('store.news_publish.field_banner')}</Label>
                  <label
                    htmlFor="news-banner"
                    className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground hover:border-primary/50"
                  >
                    <ImagePlus className="h-4 w-4" />
                    {banner ? banner.name : t('store.news_publish.banner_hint')}
                  </label>
                  <input
                    id="news-banner"
                    type="file"
                    accept={TIPOS.join(',')}
                    className="sr-only"
                    onChange={(e) => escolherBanner(e.target.files?.[0] ?? null)}
                  />
                </div>

                <div className="flex justify-end">
                  <Button type="submit" disabled={enviando}>
                    <Send className="mr-2 h-4 w-4" />
                    {enviando ? t('store.news_publish.sending') : t('store.news_publish.submit')}
                  </Button>
                </div>
              </form>
              </Caixa>
            </CardContent>
          </Card>
          </Caixa>
        )}
      </div>
    </div>
  );
}
